import { Link } from "react-router-dom";
import { Briefcase, ClipboardList, Rocket, Zap, ArrowRight, ArrowLeft } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useSiteImages } from "../hooks/use-site-images";

const ICONS = [Briefcase, ClipboardList, Rocket, Zap];
const HERO_IMAGES = [
  "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1000&q=80",
  "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1000&q=80",
  "https://images.unsplash.com/photo-1445019980597-93fa8acb246c?auto=format&fit=crop&w=1000&q=80",
  "https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=1000&q=80",
];

export default function BusinessHubPage() {
  const { t, isRTL } = useLang();
  const siteImage = useSiteImages();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  return (
    <div data-testid="business-hub-page">
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-cyan-50 via-white to-red-50/40 overflow-hidden">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 text-center relative">
          <div className="inline-block text-[10px] tracking-[0.35em] text-[#ef4444] font-semibold mb-4 uppercase">
            {t.businessHub.eyebrow}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-5 text-[#0891b2] max-w-3xl mx-auto">
            {t.businessHub.title}
          </h1>
          <p className="text-slate-600 text-lg leading-relaxed max-w-2xl mx-auto">
            {t.businessHub.subtitle}
          </p>
        </div>
      </section>

      {/* Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 gap-6">
            {t.businessHub.items.map((it, i) => {
              const Icon = ICONS[i] || Briefcase;
              return (
                <Link
                  key={i}
                  to={it.link}
                  data-testid={`hub-page-card-${i}`}
                  className="group relative bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-[#ef4444] hover:shadow-2xl transition-all"
                >
                  <div className="grid grid-cols-5">
                    <div className="col-span-2 relative aspect-square overflow-hidden bg-slate-100">
                      <img
                        src={siteImage(`business_hub_card_${i + 1}`, HERO_IMAGES[i])}
                        alt={it.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-tr from-[#0891b2]/60 via-transparent to-transparent" />
                      <div className="absolute top-4 start-4 w-12 h-12 rounded-xl bg-white shadow-lg flex items-center justify-center">
                        <Icon className="w-5 h-5 text-[#ef4444]" />
                      </div>
                    </div>
                    <div className="col-span-3 p-6 lg:p-8 flex flex-col">
                      <h3 className="font-display text-xl lg:text-2xl text-[#0891b2] mb-3 leading-snug">{it.title}</h3>
                      <p className="text-slate-600 text-sm leading-relaxed mb-5 flex-1">{it.text}</p>
                      <div className="inline-flex items-center gap-2 text-[#ef4444] text-xs font-semibold uppercase tracking-widest">
                        {it.cta}
                        <Arrow className="w-3.5 h-3.5 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
